import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';

import HeadSub from '../../../../components/comm/HeadSub';
import ConfirmModal from '../../../../components/comm/ConfirmModal';
import FoodImageSlider from '../../../../components/home/food-record/FoodImageSlider';
import NutritionAnalysisCard from '../../../../components/home/food-record/NutritionAnalysisCard';
import FoodItemCard from '../../../../components/home/food-record/FoodItemCard';

import URL from '../../../../constants/url';
import useBackTo from '../../../../hook/useBackTo';
import { loadMeal, saveMeal } from '../../../../utils/mealStorage';
import { getImage, delImage } from '../../../../utils/imageStore';
import style from './FoodRecordDetail.module.css';

import axiosApi from '../../../../api/daonAxios';

import {IMAGE_URL} from '../../../../constants/url';

const MEAL_LABELS = {
    BKFST: '아침',
    LUNCH:     '점심',
    DINNR:    '저녁',
    SNACK:     '간식',
};

const getImageSize = (imageUrl) => {
    return new Promise((resolve, reject) => {
        const image = new Image();

        image.onload = () => {
            resolve({
                width: image.naturalWidth,
                height: image.naturalHeight,
            });
        };

        image.onerror = () => {
            reject(new Error('이미지 크기를 확인할 수 없습니다.'));
        };

        image.src = imageUrl;
    });
};
// 영양정보 0 상태
const EMPTY_NUTRITION = { energy: 0, sodium: 0, protein: 0, carbohydrate: 0, fat: 0 };


const FoodRecordDetail = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const location = useLocation();
    const { state } = location;

    const ntktmTyCd = state?.ntktmTyCd;
    const recDe = state?.recDe;

    const [toDayCalorHist, setToDayCalorHist] = useState([]);

    const imageObjectUrlsRef = useRef([]);

    const [slides, setSlides] = useState([]);

    const [loading, setLoading] = useState(true);  // 로딩 추가
    const loadingTimerRef = useRef(null);

    const startLoading = () => {
        clearTimeout(loadingTimerRef.current);
        // 300ms 안에 끝나면 스켈레톤을 아예 띄우지 않는다
        loadingTimerRef.current = setTimeout(() => setLoading(true), 400);
    };

    const stopLoading = () => {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = setTimeout(() => setLoading(false), 500);
    };

    useEffect(() => () => clearTimeout(loadingTimerRef.current), []);

    useEffect(() => {
        if (!ntktmTyCd || !recDe) {
            setLoading(false);
            return;
        }
        getDietMainData(ntktmTyCd, recDe);
    },[ntktmTyCd, recDe])


    const getDietMainData = async (ntktmTyCd, recDe) => {
        const jToken = localStorage.getItem("jToken");

        startLoading()

        const requestOption = {
            params : {
                ntktmTyCd : ntktmTyCd,
                recDe : recDe
            },
            headers: {
                "Authorization": `Bearer ${jToken}`,
            },
        };

        await axiosApi.get('/diet/toDayCalorHistList',requestOption)
        .then(async (res) => {
            if(res.status === 200) {
                const historyList = res.data.result ?? [];
                setToDayCalorHist(historyList);
                // 기존 Object URL 정리
                imageObjectUrlsRef.current.forEach((url) => {
                    window.URL.revokeObjectURL(url);
                });

                const loaded = await loadServerSlides(historyList);

                const urlSlideIndex = Number(searchParams.get('slide')) || 0;

                const safeSlideIndex = Math.min(
                    urlSlideIndex,
                    Math.max(loaded.slides.length - 1, 0)
                );

                imageObjectUrlsRef.current = loaded.objectUrls;
                setSlides(loaded.slides);
                setActiveSlideIndex(safeSlideIndex);
                // setActiveSlideIndex(0);
            } else {
                console.log("~?")
            }
        })
        .catch(error => {
            console.error(error);
        }).finally(() => {
                stopLoading()
        })
    }

    const loadServerSlides = async (historyList) => {
        const jToken = localStorage.getItem('jToken');
        const objectUrls = [];
        const result = [];

        for (const history of historyList) {

            const calorRecList = history.calorRecList ?? [];

            for (const calorRec of calorRecList) {

                const fileList = calorRec.fileList ?? [];
                const foodLensList = calorRec.foodLensList ?? [];

                /*
                * 기존 FoodItemCard 구조를 최대한 유지하기 위해
                * 새 foodLens 구조를 기존 calorRecDtl 형태로 변환
                */
                const foodList = foodLensList.map((food) => {
                    const candidate = food.foodLensCnddteList?.[0] ?? null;
                    const countDetail =
                        candidate?.foodLensCnddteDtlList?.[0] ?? null;

                    return {
                        id: food.foodLensId,
                        foodLensId: food.foodLensId,
                        calorRecId: calorRec.id,

                        name: food.name,
                        fullName: food.fullName,

                        foodName:
                            candidate?.foodName ??
                            food.name ??
                            '',

                        counts:
                            candidate?.counts ??
                            countDetail?.counts ??
                            '',

                        eatAmount:
                            food.eatAmount ?? 0,

                        servingSize:
                            candidate?.servingSize ??
                            countDetail?.servingSize ??
                            0,

                        unit:
                            candidate?.unit ?? '',

                        energy: Number(candidate?.energy) || 0,
                        sodium: Number(candidate?.sodium) || 0,
                        protein: Number(candidate?.protein) || 0,
                        carbohydrate: Number(candidate?.carbohydrate) || 0,
                        fat: Number(candidate?.fat) || 0,

                        positionXmin: food.positionXmin,
                        positionYmin: food.positionYmin,
                        positionXmax: food.positionXmax,
                        positionYmax: food.positionYmax,

                        lensOrglData: food.lensOrglData,

                        foodLens: food,
                        candidate,
                        countDetail,
                        estimated:
                            candidate?.foodLensCnddteEstmtd ?? null,
                    };
                });

                /*
                * 파일이 없더라도 calorRec 데이터는 존재할 수 있으므로
                * null을 하나 넣어서 슬라이드를 생성한다.
                */
                const slideFiles = fileList.length > 0
                    ? fileList
                    : [null];

                for (const file of slideFiles) {

                    let imageUrl = null;
                    let width = null;
                    let height = null;

                    /*
                    * 실제 첨부파일이 있을 때만 이미지 조회
                    */
                    if (file) {
                        try {
                            const imageRes = await axiosApi.get(
                                IMAGE_URL + `${file.atchFileStr}&fileSn=${file.fileSn}`,
                                {
                                    responseType: 'blob',
                                    headers: {
                                        Authorization: `Bearer ${jToken}`,
                                    },
                                },
                            );

                            imageUrl = window.URL.createObjectURL(imageRes.data);
                            objectUrls.push(imageUrl);

                            try {
                                const imageSize = await getImageSize(imageUrl);

                                width = imageSize.width;
                                height = imageSize.height;

                            } catch (error) {
                                console.error(
                                    '이미지 크기 조회 실패',
                                    file.fileId,
                                    error
                                );

                                window.URL.revokeObjectURL(imageUrl);

                                imageUrl = null;
                                width = null;
                                height = null;
                            }

                        } catch (error) {
                            console.error(
                                '이미지 조회 실패',
                                file.fileId,
                                error
                            );
                        }
                    }

                    result.push({
                        historyId: history.id,
                        calorRecId: calorRec.id,

                        // 파일이 없으면 null
                        fileId: file?.fileId ?? null,
                        fileSn: file?.fileSn ?? null,

                        imageUrl,

                        calorRec: {
                            energy: Number(calorRec.energy) || 0,
                            sodium: Number(calorRec.sodium) || 0,
                            protein: Number(calorRec.protein) || 0,
                            carbohydrate: Number(calorRec.carbohydrate) || 0,
                            fat: Number(calorRec.fat) || 0,
                        },

                        calorRecDtlList: foodList,

                        /*
                        * 이미지가 있을 때만 음식 위치 라벨 생성
                        */
                        labels:
                            imageUrl && width && height
                                ? foodLensList.map((food) => ({
                                    name: food.name,

                                    x:
                                        ((Number(food.positionXmin) +
                                            Number(food.positionXmax)) /
                                            2) /
                                        width,

                                    y:
                                        ((Number(food.positionYmin) +
                                            Number(food.positionYmax)) /
                                            2) /
                                        height,
                                }))
                                : [],
                    });
                }
            }
        }

        return {
            slides: result,
            objectUrls,
        };
    };


    /////////////////////////////////
    const mealLabel = MEAL_LABELS[ntktmTyCd] || '식단';

    // 촬영 결과는 localStorage(끼니 메타) + IndexedDB(사진 dataUri) 에 나눠 저장돼 있다.
    // 서버 조회 API 가 생기면 이 초기값만 교체하면 된다.
    
    const initialSlideIndex = Number(searchParams.get('slide')) || 0;
    const [activeSlideIndex, setActiveSlideIndex] = useState(initialSlideIndex);
    
    const [deleteTarget, setDeleteTarget] = useState(null);

    const [confirmOpen, setConfirmOpen] = useState(false)

    const activeSlide = slides[activeSlideIndex] ?? null;

    const calorRec = activeSlide?.calorRec ?? EMPTY_NUTRITION;
    const calorRecDtlList = activeSlide?.calorRecDtlList ?? [];

    const isEmpty = calorRecDtlList.length === 0;

    

    // X 버튼 → 삭제 확인 모달 오픈
    const handleRequestRemove = (item) => {
        setDeleteTarget(item);
        setConfirmOpen(true);    
        // handleConfirmDelete(item.foodLensId);
    };

    // 모달 확인 → 실제 삭제
    const handleConfirmDelete = (foodLensId) => {
        setDeleteTarget(null);
        setConfirmOpen(false);
        deleteFoodLens(foodLensId);
    }

    const handleCancelDelete = () => {
        setDeleteTarget(null);
        setConfirmOpen(false);
    };

    const handleClickName = (calorRecDtl) => {
        // navigate(URL.FOOD_ITEM_DETAIL, {
        //     state: {
        //         calorRecDtl,
        //         slideIndex: activeSlideIndex,
        //     },
        // });
        navigate(
            `${URL.FOOD_ITEM_DETAIL}?calorRecId=${calorRecDtl.calorRecId}&foodLensId=${calorRecDtl.foodLensId}&activeSlideIndex=${activeSlideIndex}&ntktmTyCd=${ntktmTyCd}&recDe=${recDe}`,
            { state: { imageUrl: activeSlide?.imageUrl ?? null }}
        );
    };

    // 추가하기 / 빈 사진 슬롯 → 촬영 화면 (촬영·인식·저장은 PhotoCapture 가 담당)
    const goToCapture = () => {
        navigate(`${URL.FOOD_PHOTO_CAPTURE}?meal=${ntktmTyCd}&date=${recDe}`);
    };

    const handleSlideChange = (index) => {
        setActiveSlideIndex(index);

        const params = new URLSearchParams(searchParams);

        params.set('slide', index);

        navigate(
            {
                pathname: location.pathname,
                search: params.toString(),
            },
            {
                replace: true,
                state: location.state,
            }
        );
    };

    const deleteFoodLens = useCallback((foodLensId) => {
        const url = "/diet/foodLens";
        const jToken = localStorage.getItem('jToken');
        const reqOptions = { 
            data : {foodLensId : foodLensId},
            headers: { Authorization: `Bearer ${jToken}` } 
        };
        axiosApi.delete(url,reqOptions)
        .then((res) => {
            if(res.status === 200) {
                getDietMainData(ntktmTyCd, recDe);
            } else {
            }
        })
        .catch(err => {
            console.error("", err);
        })
    },[ntktmTyCd, recDe]);

    const onBack = useBackTo(URL.FOOD_RECORD_MAIN);

    return (
        <>
            <HeadSub title={mealLabel} onBack={onBack}/>

            <div id="sub-content">
                <div className="inner-wrap">
                    <FoodImageSlider images={slides} activeIndex={activeSlideIndex} onUpload={goToCapture} onSlideChange={handleSlideChange} loading={loading}/>
                    <NutritionAnalysisCard calorRec={calorRec} loading={loading}/>

                    {loading ? (
                        <ul className={style.itemList}>
                            {[0, 1, 2].map((i) => (
                                <li key={i}>
                                    <div className={style.skeletonCard} />
                                </li>
                            ))}
                        </ul>
                    ) : isEmpty ? (
                        <div className={style.itemEmpty}>
                            <p>등록된 음식이 없습니다</p>
                        </div>
                    ) : (
                        <ul className={style.itemList}>
                            {calorRecDtlList.map((it) => (
                                <li key={it.id}>
                                    <FoodItemCard
                                        item={it}
                                        onClickName={() => handleClickName(it)}
                                        onRemove={() => handleRequestRemove(it)}
                                    />
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            <div className={style.bottomFixed}>
                <button type="button" className={style.addBtn} onClick={goToCapture}>
                    추가하기
                </button>
            </div>

            {/* 삭제 확인 모달 */}
            <ConfirmModal
                open={confirmOpen}
                title="음식 삭제"
                message={
                    deleteTarget
                        ? <>'<b>{deleteTarget.name}</b>'을<br />삭제하시겠습니까?</>
                        : ''
                }
                confirmText="삭제"
                cancelText="취소"
                onConfirm={() => {handleConfirmDelete(deleteTarget.foodLensId)}}
                onCancel={handleCancelDelete}
                confirmType="primary"
            />
        </>
    );
};

export default FoodRecordDetail;
